import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  TablePagination,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Close } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CustomerType {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

const CustomerTypesPage: React.FC = () => {
  const { t } = useTranslation();
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [editingCustomerType, setEditingCustomerType] =
    useState<CustomerType | null>(null);
  const [deletingCustomerType, setDeletingCustomerType] =
    useState<CustomerType | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [newCustomerType, setNewCustomerType] = useState<
    Omit<CustomerType, 'id' | 'createdAt' | 'updatedAt'>
  >({
    name: '',
  });

  // Load customer types from API
  useEffect(() => {
    const loadCustomerTypes = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getCustomerTypes();
        setCustomerTypes(response.customerTypes);
      } catch (err: any) {
        setError(err.message || t('customerTypes.errors.failedToLoad'));
        console.error('Error loading customer types:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomerTypes();
  }, []);

  const handleEdit = (id: string) => {
    const customerType = customerTypes.find(ct => ct.id === id);
    if (customerType) {
      setEditingCustomerType(customerType);
      setIsEditModalOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    const customerType = customerTypes.find(ct => ct.id === id);
    if (customerType) {
      setDeletingCustomerType(customerType);
      setIsDeleteModalOpen(true);
    }
  };

  const handleAddCustomerType = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setNewCustomerType({
      name: '',
    });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCustomerType(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingCustomerType(null);
  };

  const handleSaveCustomerType = async () => {
    try {
      const createdCustomerType = await apiService.createCustomerType({
        name: newCustomerType.name,
      });

      // Add the new customer type to the list
      setCustomerTypes(prev => [...prev, createdCustomerType]);

      // Show success message
      setSnackbar({
        open: true,
        message: t('customerTypes.messages.createdSuccess'),
        severity: 'success',
      });

      handleCloseModal();
    } catch (err: any) {
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customerTypes.errors.failedToCreate'),
        severity: 'error',
      });
    }
  };

  const handleUpdateCustomerType = async () => {
    if (!editingCustomerType) return;

    try {
      const updatedCustomerType = await apiService.updateCustomerType(
        editingCustomerType.id,
        {
          name: editingCustomerType.name,
        },
      );

      // Update the customer type in the list
      setCustomerTypes(prev =>
        prev.map(ct =>
          ct.id === editingCustomerType.id ? updatedCustomerType : ct,
        ),
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('customerTypes.messages.updatedSuccess'),
        severity: 'success',
      });

      handleCloseEditModal();
    } catch (err: any) {
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customerTypes.errors.failedToUpdate'),
        severity: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingCustomerType) return;

    try {
      await apiService.deleteCustomerType(deletingCustomerType.id);

      // Remove the customer type from the list
      setCustomerTypes(prev =>
        prev.filter(ct => ct.id !== deletingCustomerType.id),
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('customerTypes.messages.deletedSuccess'),
        severity: 'success',
      });
    } catch (err: any) {
      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customerTypes.errors.failedToDelete'),
        severity: 'error',
      });
    } finally {
      // Always close modal
      handleCloseDeleteModal();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange =
    (field: keyof typeof newCustomerType) =>
      // eslint-disable-next-line no-undef
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewCustomerType(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleEditInputChange =
    (field: keyof CustomerType) =>
      // eslint-disable-next-line no-undef
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingCustomerType) {
        setEditingCustomerType(prev =>
          prev
            ? {
                ...prev,
                [field]: event.target.value,
              }
            : null,
        );
      }
    };

  // Pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    // eslint-disable-next-line no-undef
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCustomerTypes = customerTypes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('customerTypes.title')}
        </Typography>
        <Alert variant="filled" severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('customerTypes.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Search and Add Customer Type Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              mb: 3,
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCustomerType}
              sx={{ minWidth: '180px' }}
            >
              {t('customerTypes.addCustomerType')}
            </Button>
          </Box>

          {/* Table */}
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="h6">{t('customerTypes.table.name')}</Typography></TableCell>
                  <TableCell><Typography variant="h6">{t('customerTypes.table.createdAt')}</Typography></TableCell>
                  <TableCell><Typography variant="h6"></Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomerTypes.map(customerType => (
                  <TableRow key={customerType.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {customerType.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{customerType.createdAt}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(customerType.id)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(customerType.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={customerTypes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Add Customer Type Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('customerTypes.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('customerTypes.modals.add.name')}</InputLabel>
              <OutlinedInput
                value={newCustomerType.name}
                onChange={handleInputChange('name')}
                label={t('customerTypes.modals.add.name')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveCustomerType}
            variant="contained"
            color="primary"
          >
            {t('customerTypes.modals.add.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Type Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('customerTypes.modals.edit.title')}</Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('customerTypes.modals.edit.name')}</InputLabel>
              <OutlinedInput
                value={editingCustomerType?.name || ''}
                onChange={handleEditInputChange('name')}
                label={t('customerTypes.modals.edit.name')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateCustomerType}
            variant="contained"
            color="primary"
          >
            {t('customerTypes.modals.edit.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('customerTypes.modals.delete.title')}</Typography>
            <IconButton onClick={handleCloseDeleteModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography>
            {t('customerTypes.modals.delete.confirm')} "
            {deletingCustomerType?.name}"? {t('customerTypes.modals.delete.warning')}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            {t('customerTypes.modals.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerTypesPage;
